import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { companyFilterData, contactFilterData, DEFAULT_EXPERIENCE_DURATION } from "./filterComponentData";
import { dataSearchState } from "../state";
import { verifyBulkActionPerformedAndAllow } from "../DataSearchUtils";
import { JOB_FUNCTION_CATEGORIES } from "./jobFunctionData";
import { FUNDING_CATEGORIES } from "./fundingData";
import { DEPARTMENT_ID_TO_LABEL } from "./EmployeesByDepartmentInputs";

const departmentIdToLabel = {};
(JOB_FUNCTION_CATEGORIES || []).forEach(cat => {
  (cat.items || []).forEach(item => { departmentIdToLabel[item.id] = item.label; });
});
const fundingIdToLabel = {};
(FUNDING_CATEGORIES || []).forEach(cat => {
  (cat.items || []).forEach(item => { fundingIdToLabel[item.id] = item.label; });
});

const getDefaultFilterState = () => ({
  confidenceScore: [],
  excludeTeamOwns: true,
  industry: [],
  sector: [],
  employeeCount: [],
  revenue: [],
  department: [],
  funding: [],
  fundingTotalAmountStart: null,
  fundingTotalAmountEnd: null,
  fundingLastAmountStart: null,
  fundingLastAmountEnd: null,
  fundingDurationStart: null,
  fundingDurationEnd: null,
  employeesByDepartment: [],
  growthByDepartment: [],
  companyType: [],
  foundedYearStart: null,
  foundedYearEnd: null,
  technologies: [],
  level: [],
  title: [],
  titleExactMatch: false,
  companyDomain: [],
  companyName: [],
  companyDomainList: [],
  companyNameList: [],
  companySearchType: "domain",
  companyKeywords: [],
  contactKeywords: [],
  profileBadge: [],
  ...DEFAULT_EXPERIENCE_DURATION,
  country: [],
  state: [],
  city: [],
  companyCountry: [],
  companyState: [],
  companyCity: [],
  name: ""
});

const resetFilterData = (id, checked = false) => {
  [...companyFilterData, ...contactFilterData].forEach(item => {
    if (item?.id === id && item?.options) {
      item.options.forEach(opt => {
        opt.checked = checked;
      });
    }
  });
};

const LIST_FILTER_IDS = [
  "companydomain",
  "location",
  "companylocation",
  "companykeywords",
  "contactkeywords",
  "industry",
  "technologies"
];

const getOptionLabel = (filterDef, valueId) => {
  const id = filterDef?.id;
  if (id === "department") return departmentIdToLabel[valueId] ?? valueId;
  if (id === "funding") return fundingIdToLabel[valueId] ?? valueId;
  const opts = filterDef?.options || [];
  const byId = opts.find(o => o.id === valueId || o.value === valueId);
  if (!byId) return valueId;
  if (id === "employeeCount") return byId.id ?? byId.value ?? valueId;
  return byId.value ?? byId.id ?? valueId;
};

// Tooltip label for chips above the results table.
// Uses more specific wording for certain filters (company name vs website, company/contact keywords, etc.).
const getChipTooltipLabel = (filterId, filterLabel) => {
  const id = (filterId || "").toLowerCase();

  if (id === "companydomain") {
    const st = (dataSearchState?.filterComponentData?.companySearchType || "domain").toLowerCase();
    if (st === "name" || st === "namelist") return "Company name";
    if (st === "domain" || st === "domainlist") return "Company Website";
    return filterLabel;
  }

  if (id === "companykeywords") return "Company keywords";
  if (id === "contactkeywords") return "Contact keywords";

  return filterLabel;
};

const ActiveFiltersBar = observer(({ setTableLoading = () => {} }) => {
  const allFilterDefs = [...companyFilterData, ...contactFilterData];
  const [tooltip, setTooltip] = useState(null);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const chipsContainerRef = useRef(null);
  const chipsWrapRef = useRef(null);

  const showTooltip = useCallback((e, filterLabel) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      label: filterLabel,
      left: rect.left + rect.width / 2,
      top: rect.top
    });
  }, []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  const hasExperienceDurationSet = () =>
    Object.keys(DEFAULT_EXPERIENCE_DURATION).some(
      k => (Number(dataSearchState?.filterComponentData?.[k]) || 0) > 0
    );

  const hasFundingRangeSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const hasDuration = (d.fundingDurationStart != null && d.fundingDurationStart !== "") || (d.fundingDurationEnd != null && d.fundingDurationEnd !== "");
    const hasLastAmount = d.fundingLastAmountStart != null || d.fundingLastAmountEnd != null;
    const hasTotalAmount = d.fundingTotalAmountStart != null || d.fundingTotalAmountEnd != null;
    return hasDuration || hasLastAmount || hasTotalAmount;
  };

  const getActiveValueChips = () => {
    const chips = [];
    allFilterDefs.forEach(def => {
      const id = def?.id;
      const label = def?.name;
      if (id === "titleExactMatch" || id === "excludeTeamOwns") return;

      if (id === "experience") {
        if (hasExperienceDurationSet()) {
          chips.push({
            filterId: "experience",
            filterLabel: "Experience",
            valueId: "experience",
            valueLabel: "Experience",
            tooltipLabel: getChipTooltipLabel("experience", "Experience")
          });
        }
        return;
      }

      if (LIST_FILTER_IDS.includes(id?.toLowerCase())) {
        let list;
        if (id?.toLowerCase() === "companydomain") {
          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
          if (st === "domainList" && (dataSearchState?.filterComponentData?.companyDomainList?.length > 0)) {
            list = ["Company Domain List"];
          } else if (st === "nameList" && (dataSearchState?.filterComponentData?.companyNameList?.length > 0)) {
            list = ["Company Name List"];
          } else {
            list = st === "name"
              ? (dataSearchState?.filterComponentData?.companyName || [])
              : (dataSearchState?.selectedFilteredList?.[id]?.length ? dataSearchState?.selectedFilteredList?.[id] : (dataSearchState?.filterComponentData?.companyDomain || []));
          }
        } else {
          list = dataSearchState?.selectedFilteredList?.[id]?.length
            ? dataSearchState?.selectedFilteredList?.[id]
            : (dataSearchState?.filterComponentData?.[id] || []);
        }
        const tooltipLabel = getChipTooltipLabel(id, label);
        list.forEach(item => {
          const valueId = typeof item === "object" ? item?.id : item;
          const valueLabel = typeof item === "object" ? (item?.name ?? item?.value ?? valueId) : item;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel
          });
        });
      } else if (id === "funding") {
        const arr = dataSearchState?.filterComponentData?.[id] || [];
        arr.forEach(valueId => {
          const valueLabel = getOptionLabel(def, valueId);
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
        if (hasFundingRangeSet()) {
          chips.push({
            filterId: "funding",
            filterLabel: label,
            valueId: "fundingRanges",
            valueLabel: "Date & amount ranges",
            tooltipLabel: getChipTooltipLabel("funding", label)
          });
        }
      } else if (id === "employeesByDepartment") {
        const arr = dataSearchState?.filterComponentData?.employeesByDepartment || [];
        arr.forEach((row, index) => {
          const fn = row.function || [];
          if (fn.length === 0 || (row.start == null && row.end == null)) return;
          const deptLabels = fn.map(fid => DEPARTMENT_ID_TO_LABEL[fid] || fid).join(", ");
          const rangeStr = [row.start != null ? row.start : "", row.end != null ? row.end : ""].filter(Boolean).join("-");
          const valueLabel = rangeStr ? `${deptLabels}: ${rangeStr}` : deptLabels;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: `empDept_${index}`,
            valueLabel: valueLabel || `Department ${index + 1}`,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      } else if (id === "growthByDepartment") {
        const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
        arr.forEach((row, index) => {
          const fn = row.function || [];
          if (fn.length === 0 || (row.start == null && row.end == null && !row.timeFrame)) return;
          const deptLabels = fn.map(fid => DEPARTMENT_ID_TO_LABEL[fid] || fid).join(", ");
          const rangeStr = [row.start != null ? `${row.start}%` : "", row.end != null ? `${row.end}%` : ""]
            .filter(Boolean)
            .join("-");
          const tf = row.timeFrame;
          const tfLabel =
            tf === "THREE" ? "3" :
            tf === "SIX" ? "6" :
            tf === "TWELVE" ? "12" :
            tf === "TWENTY_FOUR" ? "24" :
            "";
          const suffix = tfLabel ? `, ${tfLabel}m` : "";
          const base = rangeStr ? `${deptLabels}: ${rangeStr}` : deptLabels;
          const valueLabel = `${base}${suffix}`;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: `growthDept_${index}`,
            valueLabel: valueLabel || `Growth ${index + 1}`,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      } else if (id === "foundedYear") {
        const d = dataSearchState?.filterComponentData || {};
        const start = d.foundedYearStart;
        const end = d.foundedYearEnd;
        if (start != null || end != null) {
          const rangeStr = [start != null ? start : "", end != null ? end : ""]
            .filter(Boolean)
            .join("-");
          const valueLabel = rangeStr || "Founded year";
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: "foundedYear",
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        }
      } else {
        const arr = dataSearchState?.filterComponentData?.[id];
        if (!Array.isArray(arr) || arr.length === 0) return;
        arr.forEach(valueId => {
          const valueLabel = getOptionLabel(def, valueId);
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      }
    });
    return chips;
  };

  const clearSingleValue = (filterId, valueId) => {
    const performClear = () => {
      if (filterId === "experience") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          ...DEFAULT_EXPERIENCE_DURATION
        });
        return;
      }
      if (filterId === "funding" && valueId === "fundingRanges") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          fundingTotalAmountStart: null,
          fundingTotalAmountEnd: null,
          fundingLastAmountStart: null,
          fundingLastAmountEnd: null,
          fundingDurationStart: null,
          fundingDurationEnd: null
        });
        return;
      }
      if (filterId === "employeesByDepartment" && String(valueId).startsWith("empDept_")) {
        const index = parseInt(String(valueId).replace("empDept_", ""), 10);
        if (!Number.isNaN(index)) {
          const arr = (dataSearchState?.filterComponentData?.employeesByDepartment || []).filter((_, i) => i !== index);
          dataSearchState.setFilterComponentData({
            ...dataSearchState.filterComponentData,
            employeesByDepartment: arr
          });
        }
        return;
      }
      if (filterId === "growthByDepartment" && String(valueId).startsWith("growthDept_")) {
        const index = parseInt(String(valueId).replace("growthDept_", ""), 10);
        if (!Number.isNaN(index)) {
          const arr = (dataSearchState?.filterComponentData?.growthByDepartment || []).filter((_, i) => i !== index);
          dataSearchState.setFilterComponentData({
            ...dataSearchState.filterComponentData,
            growthByDepartment: arr
          });
        }
        return;
      }
      if (filterId === "foundedYear" && valueId === "foundedYear") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          foundedYearStart: null,
          foundedYearEnd: null
        });
        return;
      }
      if (LIST_FILTER_IDS.includes(filterId?.toLowerCase())) {
        let list;
        let dataKey = filterId;
        let next;
        if (filterId?.toLowerCase() === "companydomain") {
          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
          if (st === "domainList") dataKey = "companyDomainList";
          else if (st === "nameList") dataKey = "companyNameList";
          else dataKey = st === "name" ? "companyName" : "companyDomain";
          list = dataSearchState?.filterComponentData?.[dataKey] || [];
          if (dataKey === "companyDomainList" || dataKey === "companyNameList") {
            next = [];
          } else {
            next = list.filter(item => (typeof item === "object" ? item?.id : item) !== valueId);
          }
        } else {
          list = dataSearchState?.selectedFilteredList?.[filterId]?.length
            ? dataSearchState?.selectedFilteredList?.[filterId]
            : (dataSearchState?.filterComponentData?.[filterId] || []);
          next = list.filter(item => (typeof item === "object" ? item?.id : item) !== valueId);
        }
        dataSearchState.setSelectedFilteredList({
          ...dataSearchState.selectedFilteredList,
          [filterId]: next
        });
        const idsOrNames = (dataKey === "companyDomainList" || dataKey === "companyNameList") ? next : next.map(item => (typeof item === "object" ? item?.id ?? item?.name : item));
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          [dataKey]: (dataKey === "companyDomainList" || dataKey === "companyNameList") ? next : idsOrNames
        });
      } else {
        const arr = dataSearchState?.filterComponentData?.[filterId] || [];
        const next = arr.filter(v => v !== valueId);
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          [filterId]: next
        });
      }
      if (filterId === "level" || filterId === "confidenceScore" || filterId === "profileBadge") resetFilterData(filterId, false);
      // Do not run search, set loading, or change page — show stale overlay; user clicks Refresh when ready
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClear);
    } else {
      performClear();
    }
  };

  const chips = getActiveValueChips();

  const measureOverflow = useCallback(() => {
    if (chipsExpanded || chips.length === 0) {
      if (chips.length === 0) setShowMoreVisible(false);
      return;
    }
    // Only show "Show more" when there are other (hidden) chips beyond what's visible
    if (chips.length <= 1) {
      setShowMoreVisible(false);
      return;
    }
    const chipsEl = chipsContainerRef.current;
    if (!chipsEl) return;
    // Content is clipped when scrollWidth exceeds visible width (clientWidth)
    const hasOverflow = chipsEl.scrollWidth > chipsEl.clientWidth;
    setShowMoreVisible(hasOverflow);
  }, [chipsExpanded, chips.length]);

  useLayoutEffect(() => {
    const runMeasure = () => measureOverflow();
    runMeasure();
    const rafId = requestAnimationFrame(runMeasure);
    return () => cancelAnimationFrame(rafId);
  }, [measureOverflow]);

  useLayoutEffect(() => {
    const chipsEl = chipsContainerRef.current;
    const wrapEl = chipsWrapRef.current;
    if (!chipsEl || !wrapEl || chips.length === 0) return;
    const ro = new ResizeObserver(() => {
      if (!chipsExpanded) measureOverflow();
    });
    ro.observe(wrapEl);
    ro.observe(chipsEl);
    return () => ro.disconnect();
  }, [chips.length, chipsExpanded, measureOverflow]);

  useEffect(() => {
    if (!chipsExpanded) return;
    const onEscape = e => {
      if (e.key === "Escape") setChipsExpanded(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [chipsExpanded]);

  if (chips.length === 0) return null;

  const tooltipPortal =
    tooltip &&
    typeof document !== "undefined" &&
    document.body &&
    ReactDOM.createPortal(
      <div
        className="activeFilterValueChipTooltipPortal"
        style={{
          position: "fixed",
          left: tooltip.left,
          top: tooltip.top,
          transform: "translate(-50%, calc(-100% - 10px))",
          pointerEvents: "none",
          zIndex: 10000
        }}
      >
        {tooltip.label}
      </div>,
      document.body
    );

  const renderChip = ({ filterId, filterLabel, valueId, valueLabel, tooltipLabel }, idx) => (
    <span
      key={`${filterId}-${valueId}-${idx}`}
      className="activeFilterValueChip"
      aria-label={filterLabel}
      onMouseEnter={e => showTooltip(e, tooltipLabel || filterLabel)}
      onMouseLeave={hideTooltip}
    >
      <span className="activeFilterValueChipLabel">{valueLabel}</span>
      <button
        type="button"
        className="activeFilterValueChipRemove"
        onClick={e => { e.stopPropagation(); clearSingleValue(filterId, valueId); }}
        aria-label={`Remove ${valueLabel}`}
      >
        <span className="material-icons">close</span>
      </button>
    </span>
  );

  const filtersOverlay =
    chipsExpanded &&
    typeof document !== "undefined" &&
    document.body &&
    ReactDOM.createPortal(
      <div className="activeFiltersOverlayWrapper">
        <div
          className="activeFiltersOverlayMask"
          onClick={() => setChipsExpanded(false)}
          onKeyDown={e => e.key === "Escape" && setChipsExpanded(false)}
          role="button"
          tabIndex={0}
          aria-label="Close filters"
        />
        <div className="activeFiltersOverlayPanel" role="dialog" aria-label="Active filters">
          <div className="activeFiltersOverlayHeader">
            <span className="activeFiltersOverlayTitle">Active filters</span>
            <button
              type="button"
              className="activeFiltersOverlayClose"
              onClick={() => setChipsExpanded(false)}
              aria-label="Show less"
            >
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="activeFiltersOverlayChips">
            {chips.map((chip, idx) => renderChip(chip, idx))}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div className="activeFiltersBar activeFiltersBarAboveTable">
        <div ref={chipsWrapRef} className="activeFiltersChipsWrap">
          <div
            ref={chipsContainerRef}
            className="activeFiltersChips"
            style={{ overflow: "hidden" }}
          >
            {chips.map((chip, idx) => renderChip(chip, idx))}
          </div>
          {showMoreVisible && !chipsExpanded && (
            <button
              type="button"
              className="activeFiltersChipsShowMore"
              onClick={() => setChipsExpanded(true)}
            >
              Show more
            </button>
          )}
        </div>
      </div>
      {filtersOverlay}
      {tooltipPortal}
    </>
  );
});

export { ActiveFiltersBar };
export default ActiveFiltersBar;
